<template>
  <div v-loading="pageLoading">
    <!-- ── Back navigation ─────────────────────────────────────────────────── -->
    <el-button
      type="text"
      icon="el-icon-back"
      @click="goBack"
    >
      {{ $t('stability.backToList') }}
    </el-button>

    <template v-if="config">
      <!-- Metadata: customer · order · dates -->
      <el-descriptions
        :title="localName || $t('stability.deafultStabilityName')"
        border
        :content-style="{ lineHeight: '2rem' }"
      >
        <el-descriptions-item>
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-edit-outline" />
              {{ $t('stability.meta.name') }}
            </div>
          </template>
          <el-input
            v-if="isPending"
            v-model="localName"
            size="mini"
            :placeholder="$t('stability.namePlaceholder')"
            @change="saveName"
          />
          <span v-else>{{ config.name || config.sampleId }}</span>
        </el-descriptions-item>
        <el-descriptions-item v-if="customerName">
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-office-building" />
              {{ $t('stability.meta.customer') }}
            </div>
          </template>
          {{ customerName }}
        </el-descriptions-item>
        <el-descriptions-item v-if="docNumber">
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-document" />
              {{ $t('stability.meta.docNumber') }}
            </div>
          </template>
          {{ docNumber }}
        </el-descriptions-item>
        <el-descriptions-item v-if="analysisCode">
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-box" />
              {{ $t('stability.meta.analysisCode') }}
            </div>
          </template>
          {{ analysisCode }}
        </el-descriptions-item>
        <el-descriptions-item v-if="analysisDescription">
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-document" />
              {{ $t('stability.meta.analysisDescription') }}
            </div>
          </template>
          {{ analysisDescription }}
        </el-descriptions-item>
        <el-descriptions-item v-if="orderId">
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-document" />
              {{ $t('stability.meta.order') }}
            </div>
          </template>
          #{{ orderId }}
        </el-descriptions-item>
        <!-- The last item before the protocol spans the rest of its row (the
             span is clamped to what is left), so the protocol always opens a
             row of its own, in the first column -->
        <el-descriptions-item
          :span="formattedCreatedAt || formattedConfiguredAt ? 1 : 3"
        >
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-info" />
              {{ $t('stability.meta.status') }}
            </div>
          </template>
          <el-tag
            :type="statusTagType"
            size="small"
          >
            {{ $t(`enums.stabilityStatus.${config.status}`) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item
          v-if="formattedCreatedAt"
          :span="formattedConfiguredAt ? 1 : 3"
        >
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-date" />
              {{ $t('stability.meta.createdAt') }}
            </div>
          </template>
          {{ formattedCreatedAt }}
        </el-descriptions-item>
        <el-descriptions-item
          v-if="formattedConfiguredAt"
          :span="3"
        >
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-circle-check" />
              {{ $t('stability.meta.configuredAt') }}
            </div>
          </template>
          {{ formattedConfiguredAt }}
        </el-descriptions-item>
        <!-- SMP-1624 — in every status: the protocol belongs to the whole
             study and does not wait for the samples -->
        <el-descriptions-item>
          <template slot="label">
            <div class="cell-item">
              <i class="el-icon-document" />
              {{ $t('stability.meta.protocol') }}
            </div>
          </template>
          <ProtocolFileField
            :config-id="config.id"
            :file="config.protocolFile || null"
            :can-manage="canManage"
            @uploaded="loadConfig"
          />
        </el-descriptions-item>
        <!-- el-descriptions stretches its last item over the rest of the row:
             this empty one takes the stretch, so the protocol stays as wide as
             the other items of the first column -->
        <el-descriptions-item
          :span="2"
          label-class-name="stability-meta-filler"
          content-class-name="stability-meta-filler"
        />
      </el-descriptions>

      <el-divider />

      <!-- ── Status banner ─────────────────────────────────────────────────────
           Bold title and description on separate lines, like the other LIMS
           alerts. The text goes through the default slot rather than the
           `description` prop: el-alert drops the slot when both are given, and
           the error needs the retry button under its text -->
      <el-alert
        v-if="statusBannerType"
        class="stability-status-banner"
        :type="statusBannerType"
        :title="$t(`stability.banners.${config.status}.title`)"
        :closable="false"
        show-icon
      >
        <span class="stability-status-banner__text">
          {{ $t(`stability.banners.${config.status}.description`) }}
        </span>
        <span
          v-if="config.status === 'error' && canManage"
          class="stability-status-banner__actions"
        >
          <el-button
            size="mini"
            type="danger"
            icon="el-icon-refresh-right"
            :loading="retryLoading"
            @click="retryExplosion"
          >
            {{ $t('stability.retryExplosion') }}
          </el-button>
        </span>
      </el-alert>

      <!-- ── Summary: samples, analyses and planned due dates ──────────────────
           Only once configured: before that there are no samples to show -->
      <template v-if="isConfigured">
        <StabilitySummary :match-cards="matchCards" />
        <el-divider />
      </template>

      <!-- ── Time point definition ─────────────────────────────────────────── -->
      <TimePointSection
        :config-id="config.id"
        :time-points="timePoints"
        :readonly="!isPending"
        :configured="isConfigured"
        :can-manage="canManage"
        @refresh="loadConfig"
      />

      <el-divider />

      <!-- ── Match grid: one card per match ──────────────────────────────────
           Not once configured: the summary above lists the same matches,
           read-only, with their samples and due dates -->
      <el-row
        v-if="matchCards.length && !isConfigured"
        :gutter="16"
        type="flex"
        class="time-points-row"
      >
        <el-col
          v-for="card in matchCards"
          :key="card.match.id"
          :xs="24"
          :md="12"
          class="time-point-col"
        >
          <MatchCard
            :match="card.match"
            :time-point="card.timePoint"
            :config-id="config.id"
            :readonly="!isPending"
            @refresh="loadConfig"
            @clone-requested="openCloneDialog"
          />
        </el-col>
      </el-row>

      <!-- ── Empty state ──────────────────────────────────────────────────────
           No add action here on purpose: the matches come after the time points
           have been defined in the section above -->
      <el-empty
        v-else-if="isPending"
        :description="
          timePoints.length
            ? $t('stability.matches.empty')
            : $t('stability.timePoints.defineFirst')
        "
      />

      <!-- ── Pending toolbar: add + confirm (only when cards are present) ──── -->
      <template v-if="isPending && timePoints.length">
        <el-divider />

        <el-row
          type="flex"
          align="middle"
        >
          <el-col :span="8">
            <el-button
              icon="el-icon-plus"
              @click="matchDialogVisible = true"
            >
              {{ $t('stability.matches.add') }}
            </el-button>
          </el-col>

          <el-col
            :span="8"
            style="text-align: center"
          >
            <el-tag
              v-if="timePointsWithoutMatchCount > 0"
              type="warning"
              size="small"
            >
              <i class="el-icon-warning-outline" />
              {{ $t('stability.warnings.timePointsWithoutMatch') }}
            </el-tag>
            <el-tag
              v-if="matchesWithoutAnalysisCount > 0"
              type="warning"
              size="small"
            >
              <i class="el-icon-warning-outline" />
              {{ $t('stability.warnings.matchesWithoutAnalysis') }}
            </el-tag>
          </el-col>

          <el-col
            :span="8"
            style="text-align: right"
          >
            <el-tooltip
              :disabled="canConfirm"
              :content="confirmBlockedReason"
              placement="top"
              effect="light"
            >
              <!-- span needed: tooltip on disabled buttons requires a wrapping element -->
              <span>
                <el-button
                  type="primary"
                  :disabled="!canConfirm"
                  :loading="confirmLoading"
                  @click="confirmConfiguration"
                >
                  {{ $t('stability.confirmConfig') }}
                </el-button>
              </span>
            </el-tooltip>
          </el-col>
        </el-row>
      </template>
    </template>

    <!-- ── Dialogs ───────────────────────────────────────────────────────── -->
    <MatchDialog
      v-if="config"
      :visible.sync="matchDialogVisible"
      :config-id="config.id"
      :time-points="timePoints"
      :conditions="conditions"
      @created="loadConfig"
    />

    <CloneDialog
      v-if="config"
      :visible.sync="cloneDialogVisible"
      :config-id="config.id"
      :match-id="cloneSource.matchId"
      :time-points="timePoints"
      :conditions="conditions"
      :source-time-point-id="cloneSource.sourceTimePointId"
      :source-temperature="cloneSource.sourceTemperature"
      :source-humidity="cloneSource.sourceHumidity"
      :source-matrix="cloneSource.sourceMatrix"
      @cloned="loadConfig"
    />
  </div>
</template>

<script>
  import moment from 'moment';
  import { stabilityApi } from '@/api';
  import acl from '@/services/acl';
  import MatchCard from './MatchCard.vue';
  import MatchDialog from './MatchDialog.vue';
  import TimePointSection from './TimePointSection.vue';
  import CloneDialog from './CloneDialog.vue';
  import StabilitySummary from './StabilitySummary.vue';
  import ProtocolFileField from './ProtocolFileField.vue';

  export default {
    name: 'StabilityConfiguration',
    components: {
      MatchCard,
      MatchDialog,
      TimePointSection,
      CloneDialog,
      StabilitySummary,
      ProtocolFileField,
    },

    data() {
      return {
        config: null,
        localName: '',
        pageLoading: false,
        confirmLoading: false,
        retryLoading: false,
        matchDialogVisible: false,
        cloneDialogVisible: false,
        cloneSource: {
          matchId: null,
          sourceTimePointId: null,
          sourceTemperature: 30,
          sourceHumidity: null,
          sourceMatrix: null,
        },
      };
    },

    computed: {
      isPending() {
        return this.config && this.config.status === 'pending';
      },
      // The time point file upload is allowed only in this state
      isConfigured() {
        return this.config && this.config.status === 'configured';
      },
      canManage() {
        return acl.userCanUse(['stabilityConfigManage']);
      },
      customerName() {
        return (
          this.config &&
          this.config.sample &&
          this.config.sample.order &&
          this.config.sample.order.customer &&
          this.config.sample.order.customer.name
        );
      },
      docNumber() {
        return this.config?.offerDocNumber || null;
      },
      analysisCode() {
        return this.config?.analysis?.code || null;
      },
      analysisDescription() {
        return this.config?.analysis?.description || null;
      },
      orderId() {
        return this.config && this.config.sample && this.config.sample.orderId;
      },
      formattedCreatedAt() {
        if (!this.config || !this.config.createdAt) return null;
        return moment(this.config.createdAt).format('DD/MM/YYYY HH:mm');
      },
      formattedConfiguredAt() {
        if (!this.config || !this.config.configuredAt) return null;
        return moment(this.config.configuredAt).format('DD/MM/YYYY HH:mm');
      },
      timePoints() {
        return (this.config && this.config.stabilityTimePoints) || [];
      },
      statusTagType() {
        const map = {
          pending: 'warning',
          processing: 'info',
          configured: 'success',
          error: 'danger',
        };
        return (this.config && map[this.config.status]) || '';
      },
      // No banner while pending: the page itself is the work in progress
      statusBannerType() {
        const map = {
          processing: 'info',
          configured: 'success',
          error: 'error',
        };
        return (this.config && map[this.config.status]) || null;
      },
      conditions() {
        return (this.config && this.config.stabilityConditions) || [];
      },
      matches() {
        return this.timePoints.flatMap((tp) => tp.stabilityMatches || []);
      },
      // One card per match. The include of GET /{id} carries no ORDER BY, so the
      // order is imposed here to keep the layout deterministic.
      matchCards() {
        return [...this.timePoints]
          .sort(
            (a, b) =>
              new Date(a.fromDate) - new Date(b.fromDate) ||
              a.token.localeCompare(b.token)
          )
          .flatMap((timePoint) =>
            [...(timePoint.stabilityMatches || [])]
              .sort((a, b) => this.compareConditions(a, b))
              .map((match) => ({ match, timePoint }))
          );
      },
      // The API refuses the configuration while a time point has no match
      timePointsWithoutMatchCount() {
        return this.timePoints.filter(
          (tp) => !tp.stabilityMatches || tp.stabilityMatches.length === 0
        ).length;
      },
      // ...or while a match has no analysis. The analyses hang off the match,
      // not the time point.
      matchesWithoutAnalysisCount() {
        return this.matches.filter(
          (match) =>
            !match.stabilityConfigurationAnalyses ||
            match.stabilityConfigurationAnalyses.length === 0
        ).length;
      },
      canConfirm() {
        return (
          this.timePoints.length > 0 &&
          this.timePointsWithoutMatchCount === 0 &&
          this.matchesWithoutAnalysisCount === 0
        );
      },
      confirmBlockedReason() {
        if (this.timePoints.length === 0) {
          return this.$t('stability.warnings.noTimePointsConfirm');
        }
        if (this.timePointsWithoutMatchCount > 0) {
          return this.$t('stability.warnings.timePointsWithoutMatch');
        }
        if (this.matchesWithoutAnalysisCount > 0) {
          return this.$t('stability.warnings.matchesWithoutAnalysis');
        }
        return '';
      },
    },

    created() {
      this.loadConfig();
    },

    methods: {
      goBack() {
        this.$router.push({ name: 'stabilityConfigurationsIndex' });
      },

      // Orders the matches of one time point by their condition
      compareConditions(a, b) {
        const left = a.stabilityCondition || {};
        const right = b.stabilityCondition || {};
        return (
          (left.temperature ?? 0) - (right.temperature ?? 0) ||
          (left.humidity ?? -1) - (right.humidity ?? -1) ||
          (left.matrixNorm || '').localeCompare(right.matrixNorm || '')
        );
      },

      async loadConfig() {
        this.pageLoading = true;
        try {
          const res = await stabilityApi.show(this.$route.params.id);
          // Freeze non-editable configs — no need for Vue reactivity on read-only trees
          this.config =
            res.data.status === 'pending' ? res.data : Object.freeze(res.data);
          this.localName = (this.config && this.config.name) || '';
        } catch (err) {
          this.$notify({
            type: 'error',
            showClose: true,
            message:
              (err.response &&
                err.response.data &&
                err.response.data.error &&
                err.response.data.error.message) ||
              this.$t('stability.errors.loadFailed'),
          });
        } finally {
          this.pageLoading = false;
        }
      },

      async saveName(value) {
        const trimmed = value.trim();
        if (!trimmed || trimmed === this.config.name) return;
        try {
          await stabilityApi.updateConfig(this.config.id, { name: trimmed });
          this.localName = trimmed;
        } catch (err) {
          this.localName = this.config.name || '';
          this.$notify({
            type: 'error',
            showClose: true,
            message:
              (err.response &&
                err.response.data &&
                err.response.data.error &&
                err.response.data.error.message) ||
              this.$t('stability.errors.generic'),
          });
        }
      },

      openCloneDialog({
        matchId,
        sourceTimePointId,
        sourceTemperature,
        sourceHumidity,
        sourceMatrix,
      }) {
        this.cloneSource = {
          matchId,
          sourceTimePointId,
          sourceTemperature: sourceTemperature ?? 30,
          sourceHumidity: sourceHumidity ?? null,
          sourceMatrix: sourceMatrix ?? null,
        };
        this.cloneDialogVisible = true;
      },

      async confirmConfiguration() {
        try {
          await this.$confirm(
            this.$t('stability.confirmConfigMessage'),
            this.$t('stability.confirmConfigTitle'),
            {
              confirmButtonText: this.$t('stability.confirmConfig'),
              cancelButtonText: this.$t('global.cancel'),
              // Kept here, unlike the delete confirms: this one starts the
              // sample explosion and freezes the configuration, so it earns the
              // status icon even though the theme paints it dark red
              type: 'warning',
            }
          );
        } catch {
          return;
        }

        this.confirmLoading = true;
        try {
          await stabilityApi.configure(this.config.id);
          await this.loadConfig();
          this.$root.$emit('stability:refresh-pending-count');
        } catch (err) {
          this.$notify({
            type: 'error',
            showClose: true,
            message:
              (err.response &&
                err.response.data &&
                err.response.data.error &&
                err.response.data.error.message) ||
              this.$t('stability.errors.generic'),
          });
        } finally {
          this.confirmLoading = false;
        }
      },

      async retryExplosion() {
        this.retryLoading = true;
        try {
          await stabilityApi.retry(this.config.id);
          await this.loadConfig();
        } catch (err) {
          this.$notify({
            type: 'error',
            showClose: true,
            message:
              (err.response &&
                err.response.data &&
                err.response.data.error &&
                err.response.data.error.message) ||
              this.$t('stability.errors.generic'),
          });
        } finally {
          this.retryLoading = false;
        }
      },
    },
  };
</script>

<style lang="scss">
  .el-descriptions {
    font-size: 0.9rem;

    .el-descriptions__title {
      font-size: 1.1rem;
    }

    .el-descriptions__body {
      background-color: $--color-black;
    }

    .el-descriptions-item__label.is-bordered-label {
      color: $--color-text-regular;
    }

    .el-descriptions-item__content {
      background-color: $--color-black-lighter;
      color: $--color-text-primary;
    }
  }

  // The empty cell after the protocol: no label, and blank like the page
  // background rather than drawn as an empty slot of the grid
  .el-descriptions .el-descriptions-item__cell.stability-meta-filler {
    background-color: transparent;
    border-color: transparent;
  }

  // el-alert sets margin: 0, and below the banner comes either the summary or
  // the time point section, neither with a top margin of its own
  .el-alert.stability-status-banner {
    margin-bottom: 1.5rem;
    padding: 12px 16px;

    .el-alert__title {
      font-size: 14px;
    }

    .el-alert__description {
      font-size: 13px;
      line-height: 1.5;
      margin-top: 4px;
    }
  }

  .stability-status-banner__text,
  .stability-status-banner__actions {
    display: block;
  }

  .stability-status-banner__actions {
    margin-top: 0.75rem;
  }

  .time-points-row {
    flex-wrap: wrap;
    align-items: stretch;
  }

  .time-point-col {
    margin-bottom: 1rem;
    display: flex;

    .el-card {
      flex: 1;
    }
  }

  .cell-item {
    display: flex;
    align-items: center;

    i[class^='el-icon-'] {
      font-size: 18px;
      margin-right: 6px;
    }
  }
</style>
